// TODO: consolidate with better thought out and carefully tested sorting functions here:
// https://github.com/ChatableAI/chatable-ux/blob/master/src/common/helpers/utils/sorting.js#L13
import { Entry, useEntryCRUDActions } from '@mm/state/actions/EntryCRUDActions';
import { useRef } from 'react';

const shouldChangeSortOrder = (
  newOrder: number,
  oldOrder: number,
  entryOrder: number,
  gettingUpper: boolean,
) =>
  (entryOrder > newOrder === gettingUpper || entryOrder === newOrder) &&
  !(entryOrder > oldOrder === gettingUpper);

type Props = {
  entries: Entry[];
  widgetId: number;
};

export const useSorting = ({ entries, widgetId }: Props) => {
  const { update: updateEntry } = useEntryCRUDActions();
  const itemsContainerRef = useRef<HTMLElement | null>(null);
  const itemsRefs = useRef<HTMLElement[]>([]);
  const sortedEntries = useRef<Entry[]>([]);

  const setItemsContainerRef = (ref: HTMLElement) => {
    itemsContainerRef.current = ref;
  };

  const getItemsContainerRef = () => itemsContainerRef.current;

  const setItemRef = (ref: HTMLElement) => {
    return ref && itemsRefs.current.push(ref);
  };
  const getItemsRefs = () => itemsRefs.current;
  const setSortedEntries = (entries: Entry[]) => {
    sortedEntries.current = entries.sort((a, b) => a.sortOrder - b.sortOrder);
  };
  const getSortedEntries = () => sortedEntries.current;
  const onDropEntry = (
    sourceEntry: Entry,
    targetEntry: Entry,
    top: boolean,
  ) => {
    if (sourceEntry.sortOrder === targetEntry.sortOrder) {
      return;
    }
    const gettingUpper = sourceEntry.sortOrder > targetEntry.sortOrder;
    let sortOrderCorrection = -1;
    if (gettingUpper) sortOrderCorrection += 1;
    const oldSortOrder = sourceEntry.sortOrder;
    const newSortOrder =
      targetEntry.sortOrder +
      (top ? sortOrderCorrection : sortOrderCorrection + 1);
    if (newSortOrder === sourceEntry.sortOrder) {
      return;
    }

    entries.forEach(entry => {
      if (
        shouldChangeSortOrder(
          newSortOrder,
          oldSortOrder,
          entry.sortOrder,
          gettingUpper,
        ) &&
        entry.id !== sourceEntry.id
      ) {
        // TODO: add batch updates instead of sequential.
        // Motivation - ensure consistency and minimize networks requests.
        // Sample implementation done for Page here.
        // src/editor/features/build/components/PageOverviewPane/PageOverviewPane.js#L50
        // Not doing it here now since it is a little bit less critical.
        updateEntry(widgetId, {
          ...entry,
          sortOrder: entry.sortOrder + (gettingUpper ? 1 : -1),
        });
      }
    });

    updateEntry(widgetId, {
      ...sourceEntry,
      sortOrder: newSortOrder,
    });
  };

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const localSortedEntries = getSortedEntries();
    onDropEntry(
      localSortedEntries[oldIndex],
      localSortedEntries[newIndex],
      newIndex < oldIndex,
    );
  };

  return {
    getItemsContainerRef,
    setItemsContainerRef,
    onSortEnd,
    setItemRef,
    getSortedEntries,
    setSortedEntries,
    getItemsRefs,
  };
};
